import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Auth
export const authApi = {
  login: (email, password) => axios.post(`${API_BASE}/auth/login`, { email, password }),
  register: (name, email, password) => axios.post(`${API_BASE}/auth/register`, { name, email, password }),
  google: (code, redirect_uri) => axios.post(`${API_BASE}/auth/google`, { code, redirect_uri }),
  me: () => axios.get(`${API_BASE}/auth/me`),
  updateProfile: (data) => axios.put(`${API_BASE}/auth/profile`, data),
  disconnect: (provider) => axios.post(`${API_BASE}/auth/disconnect`, { provider }),
  listSessions: () => axios.get(`${API_BASE}/auth/sessions`),
  revokeSession: (jti) => axios.delete(`${API_BASE}/auth/sessions/${jti}`),
  revokeAllSessions: (keepCurrent = true) => axios.post(`${API_BASE}/auth/sessions/revoke-all`, { keep_current: keepCurrent }),
  exportData: () => axios.get(`${API_BASE}/auth/export-data`, { responseType: 'blob' }),
  deleteAccount: (confirmEmail) => axios.post(`${API_BASE}/auth/delete-account`, { confirm_email: confirmEmail }),
};

// Google Calendar
export const calendarApi = {
  createEvent: (data) => axios.post(`${API_BASE}/calendar/events`, data),
  createReminder: (data) => axios.post(`${API_BASE}/reminders`, data),
  getSubscription: () => axios.get(`${API_BASE}/calendar/subscription`),
  resetSubscription: () => axios.post(`${API_BASE}/calendar/subscription/reset`),
};

// Drafts (unprocessed captures)
export const draftsApi = {
  list: () => axios.get(`${API_BASE}/drafts`),
  get: (id) => axios.get(`${API_BASE}/drafts/${id}`),
  create: (formData) => axios.post(`${API_BASE}/drafts`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => axios.put(`${API_BASE}/drafts/${id}`, data),
  addClip: (id, formData) => axios.post(`${API_BASE}/drafts/${id}/clips`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteClip: (id, clipId) => axios.delete(`${API_BASE}/drafts/${id}/clips/${clipId}`),
  remove: (id) => axios.delete(`${API_BASE}/drafts/${id}`),
  process: (id, teamId, formData) => axios.post(`${API_BASE}/drafts/${id}/process`, formData || new FormData(), { params: teamId ? { team_id: teamId } : {}, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }),
};

// Action Cards
export const actionCardsApi = {
  list: (params) => axios.get(`${API_BASE}/action-cards`, { params }),
  get: (id) => axios.get(`${API_BASE}/action-cards/${id}`),
  captureText: (formData) => axios.post(`${API_BASE}/action-cards/capture/text`, formData),
  captureAudio: (formData) => axios.post(`${API_BASE}/action-cards/capture/audio`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000, // 2 mins for transcription
  }),
  createManual: (data) => axios.post(`${API_BASE}/action-cards/manual`, data),
  update: (id, data) => axios.put(`${API_BASE}/action-cards/${id}`, data),
  move: (id, folderId, teamId) => axios.put(`${API_BASE}/action-cards/${id}/move`, { folder_id: folderId, team_id: teamId }),
  updateActionItems: (id, items) => axios.put(`${API_BASE}/action-cards/${id}/action-items`, items),
  delete: (id) => axios.delete(`${API_BASE}/action-cards/${id}`),
  generateEmail: (id, tone) => axios.post(`${API_BASE}/action-cards/${id}/generate-email?tone=${tone || 'professional'}`),
  refineEmail: (id, payload) => axios.post(`${API_BASE}/action-cards/${id}/refine-email`, payload),
  addEmailDraft: (id, payload) => axios.post(`${API_BASE}/action-cards/${id}/email-drafts`, payload),
  deleteEmailDraft: (id, draftId) => axios.delete(`${API_BASE}/action-cards/${id}/email-drafts/${draftId}`),
  sendEmail: (id, emailData) => axios.post(`${API_BASE}/action-cards/${id}/send-email`, emailData),
  sendAndSchedule: (id, data) => axios.post(`${API_BASE}/action-cards/${id}/send-and-schedule`, data),
  updateTask: (cardId, taskId, data) => axios.put(`${API_BASE}/action-cards/${cardId}/tasks/${taskId}`, data),
  addTask: (cardId, data) => axios.post(`${API_BASE}/action-cards/${cardId}/tasks`, data),
  deleteTask: (cardId, taskId) => axios.delete(`${API_BASE}/action-cards/${cardId}/tasks/${taskId}`),
  assignees: (cardId) => axios.get(`${API_BASE}/action-cards/${cardId}/assignees`),
  mute: (cardId) => axios.post(`${API_BASE}/action-cards/${cardId}/mute`),
  unmute: (cardId) => axios.delete(`${API_BASE}/action-cards/${cardId}/mute`),
  regenerateNotes: (cardId, templateId) => axios.post(`${API_BASE}/action-cards/${cardId}/regenerate-notes`, templateId ? { template_id: templateId } : {}),
};

// Recall.ai meeting bots
export const recallApi = {
  list: () => axios.get(`${API_BASE}/recall/bots`),
  send: (data) => axios.post(`${API_BASE}/recall/bots`, data),
  sync: (id) => axios.post(`${API_BASE}/recall/bots/${id}/sync`),
  remove: (id) => axios.delete(`${API_BASE}/recall/bots/${id}`),
};

// Tasks
export const scratchpadApi = {
  get: () => axios.get(`${API_BASE}/scratchpad`),
  save: (content) => axios.put(`${API_BASE}/scratchpad`, { content }),
  transcribe: (formData) => axios.post(`${API_BASE}/scratchpad/transcribe`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }),
};


export const tasksApi = {
  list: (params) => axios.get(`${API_BASE}/tasks`, { params }),
  get: (id) => axios.get(`${API_BASE}/tasks/${id}`),
  create: (data) => axios.post(`${API_BASE}/tasks`, data),
  subtasks: (id) => axios.get(`${API_BASE}/tasks/${id}/subtasks`),
  setParent: (id, parentTaskId) => axios.put(`${API_BASE}/tasks/${id}/parent`, { parent_task_id: parentTaskId || null }),
  update: (id, data) => axios.put(`${API_BASE}/tasks/${id}`, data),
  assign: (id, sel) => axios.put(`${API_BASE}/tasks/${id}/assign`, Array.isArray(sel) ? { assignee_user_ids: sel } : { assignee_user_id: sel || '' }),
  addCollaborator: (id, userId) => axios.post(`${API_BASE}/tasks/${id}/collaborators`, { user_id: userId }),
  removeCollaborator: (id, userId) => axios.delete(`${API_BASE}/tasks/${id}/collaborators/${userId}`),
  recordView: (id) => axios.post(`${API_BASE}/tasks/${id}/view`),
  views: (id) => axios.get(`${API_BASE}/tasks/${id}/views`),
  delete: (id) => axios.delete(`${API_BASE}/tasks/${id}`),
  mergeDuplicate: (id, intoId) => axios.post(`${API_BASE}/tasks/${id}/merge`, { into_id: intoId }),
  dismissDuplicate: (id) => axios.post(`${API_BASE}/tasks/${id}/dismiss-duplicate`),
  addTag: (id, tag) => axios.post(`${API_BASE}/tasks/${id}/tags`, { tag }),
  removeTag: (id, tag) => axios.delete(`${API_BASE}/tasks/${id}/tags/${encodeURIComponent(tag)}`),
  savedViews: () => axios.get(`${API_BASE}/tasks/saved-views`),
  saveView: (name, filters) => axios.post(`${API_BASE}/tasks/saved-views`, { name, filters }),
  deleteView: (id) => axios.delete(`${API_BASE}/tasks/saved-views/${id}`),
  listComments: (id) => axios.get(`${API_BASE}/tasks/${id}/comments`),
  addComment: (id, data) => axios.post(`${API_BASE}/tasks/${id}/comments`, data),
  addVoiceNote: (id, blob) => {
    const fd = new FormData();
    fd.append('file', blob, 'voice-note.webm');
    return axios.post(`${API_BASE}/tasks/${id}/voice`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
    });
  },
};

// Files (object storage) + central library
export const filesApi = {
  list: (params) => axios.get(`${API_BASE}/files`, { params }),
  upload: (file, { taskId, actionCardId } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (taskId) fd.append('task_id', taskId);
    if (actionCardId) fd.append('action_card_id', actionCardId);
    return axios.post(`${API_BASE}/files/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
    });
  },
  delete: (id) => axios.delete(`${API_BASE}/files/${id}`),
};

// Build an authenticated download/preview URL (token in query — img tags can't send headers).
export const fileUrl = (id) => `${API_BASE}/files/${id}/download?auth=${encodeURIComponent(sessionStorage.getItem('mk_session') || '')}`;

// Folders + Team workspace (Phase 3)
export const foldersApi = {
  list: () => axios.get(`${API_BASE}/folders`),
  create: (data) => axios.post(`${API_BASE}/folders`, data),
  rename: (id, name) => axios.put(`${API_BASE}/folders/${id}`, { name }),
  setColor: (id, color) => axios.put(`${API_BASE}/folders/${id}`, { color }),
  setEmoji: (id, emoji) => axios.put(`${API_BASE}/folders/${id}`, { emoji }),
  delete: (id) => axios.delete(`${API_BASE}/folders/${id}`),
  restore: (payload) => axios.post(`${API_BASE}/folders/restore`, payload),
};

export const templatesApi = {
  list: () => axios.get(`${API_BASE}/templates`),
  create: (data) => axios.post(`${API_BASE}/templates`, data),
  update: (id, data) => axios.put(`${API_BASE}/templates/${id}`, data),
  remove: (id) => axios.delete(`${API_BASE}/templates/${id}`),
};


export const teamsApi = {
  list: () => axios.get(`${API_BASE}/teams`),
  overview: (teamId) => axios.get(`${API_BASE}/teams/${teamId}/overview`),
  create: (name) => axios.post(`${API_BASE}/teams`, { name }),
  rename: (teamId, name) => axios.put(`${API_BASE}/teams/${teamId}`, { name }),
  setOrgTeam: (teamId, orgTeamId) => axios.put(`${API_BASE}/teams/${teamId}/org-team`, { org_team_id: orgTeamId }),
  remove: (teamId, confirmName) => axios.delete(`${API_BASE}/teams/${teamId}`, { data: { confirm_name: confirmName } }),
  saveOrder: (teamIds) => axios.put(`${API_BASE}/teams/order`, { team_ids: teamIds }),
  trash: () => axios.get(`${API_BASE}/teams/trash`),
  restore: (teamId) => axios.post(`${API_BASE}/teams/${teamId}/restore`),
  pin: (teamId) => axios.post(`${API_BASE}/teams/${teamId}/pin`),
  unpin: (teamId) => axios.delete(`${API_BASE}/teams/${teamId}/pin`),
  myActivity: (teamId) => axios.get(`${API_BASE}/teams/${teamId}/my-activity`),
  invite: (teamId, email, role) => axios.post(`${API_BASE}/teams/${teamId}/invite`, { email, ...(role ? { role } : {}) }),
  invitations: () => axios.get(`${API_BASE}/teams/invitations`),
  accept: (teamId) => axios.post(`${API_BASE}/teams/${teamId}/accept`),
  decline: (teamId) => axios.post(`${API_BASE}/teams/${teamId}/decline`),
  activity: (teamId) => axios.get(`${API_BASE}/teams/${teamId}/activity`),
  removeMember: (teamId, userId) => axios.delete(`${API_BASE}/teams/${teamId}/members/${userId}`),
  tasks: (teamId) => axios.get(`${API_BASE}/teams/${teamId}/tasks`),
  createTask: (teamId, data) => axios.post(`${API_BASE}/teams/${teamId}/tasks`, data),
  updateTask: (teamId, taskId, data) => axios.put(`${API_BASE}/teams/${teamId}/tasks/${taskId}`, data),
  assignTask: (teamId, taskId, sel) => axios.put(`${API_BASE}/teams/${teamId}/tasks/${taskId}/assign`, Array.isArray(sel) ? { assignee_user_ids: sel } : { assignee_user_id: sel }),
  addTaskCollaborator: (teamId, taskId, userId) => axios.post(`${API_BASE}/teams/${teamId}/tasks/${taskId}/collaborators`, { user_id: userId }),
  removeTaskCollaborator: (teamId, taskId, userId) => axios.delete(`${API_BASE}/teams/${teamId}/tasks/${taskId}/collaborators/${userId}`),
  deleteTask: (teamId, taskId) => axios.delete(`${API_BASE}/teams/${teamId}/tasks/${taskId}`),
  sendDigest: (teamId) => axios.post(`${API_BASE}/teams/${teamId}/digest/preview`),
  reassign: (teamId, payload) => axios.post(`${API_BASE}/teams/${teamId}/reassign`, payload),
};

// Contacts
export const contactsApi = {
  list: (params) => axios.get(`${API_BASE}/contacts`, { params }),
  get: (id) => axios.get(`${API_BASE}/contacts/${id}`),
  create: (data) => axios.post(`${API_BASE}/contacts`, data),
  update: (id, data) => axios.put(`${API_BASE}/contacts/${id}`, data),
  delete: (id) => axios.delete(`${API_BASE}/contacts/${id}`),
  getActionCards: (id) => axios.get(`${API_BASE}/contacts/${id}/action-cards`),
};

// Dashboard
export const dashboardApi = {
  get: () => axios.get(`${API_BASE}/dashboard`),
};

// Settings
export const settingsApi = {
  get: () => axios.get(`${API_BASE}/settings`),
  update: (data) => axios.put(`${API_BASE}/settings`, data),
  sendDigestPreview: (to) => axios.post(`${API_BASE}/settings/digest/preview`, { to: to || null }),
  sendAllEmailPreviews: () => axios.post(`${API_BASE}/settings/email-preview/all`),
};

// Search
export const searchApi = {
  search: (q) => axios.get(`${API_BASE}/search`, { params: { q } }),
};

// Notifications
export const notificationsApi = {
  get: () => axios.get(`${API_BASE}/notifications`),
  markRead: () => axios.post(`${API_BASE}/notifications/read`),
};

// Organization / Teams (permission & accountability layer)
export const organizationApi = {
  get: () => axios.get(`${API_BASE}/organization`),
  create: (name) => axios.post(`${API_BASE}/organization`, { name }),
  members: () => axios.get(`${API_BASE}/organization/members`),
  invite: (data) => axios.post(`${API_BASE}/organization/invite`, data),
  invites: () => axios.get(`${API_BASE}/organization/invites`),
  revokeInvite: (id) => axios.delete(`${API_BASE}/organization/invites/${id}`),
  pendingInvites: () => axios.get(`${API_BASE}/organization/pending-invites`),
  acceptInvite: (id) => axios.post(`${API_BASE}/organization/invites/${id}/accept`),
  teams: () => axios.get(`${API_BASE}/organization/teams`),
  createTeam: (data) => axios.post(`${API_BASE}/organization/teams`, data),
  getTeam: (id) => axios.get(`${API_BASE}/organization/teams/${id}`),
  updateTeam: (id, data) => axios.put(`${API_BASE}/organization/teams/${id}`, data),
  deleteTeam: (id) => axios.delete(`${API_BASE}/organization/teams/${id}`),
  addMember: (teamId, data) => axios.post(`${API_BASE}/organization/teams/${teamId}/members`, data),
  removeMember: (teamId, userId) => axios.delete(`${API_BASE}/organization/teams/${teamId}/members/${userId}`),
  assignCardTeam: (cardId, data) => axios.put(`${API_BASE}/action-cards/${cardId}/org-team`, data),
  companyReport: () => axios.get(`${API_BASE}/organization/report`),
  teamReport: (teamId) => axios.get(`${API_BASE}/organization/teams/${teamId}/report`),
};
